import { AuthService } from '@/features/auth';
import { authStorage } from '@/features/auth/config/auth-storage';
import { api } from './api';
import FetchError from './FetchError';

interface FetcherProps {
  url: string | null;
  options?: RequestInit;
  requiresAuth?: boolean;
}

interface ApiErrorResponse {
  message?: string;
  error?: string | { message?: string; code?: string };
  detail?: string;
}

export const fetcher = async <T>({
  url,
  options,
  requiresAuth,
  isBlob = false,
}: FetcherProps & { isBlob?: boolean }): Promise<T> => {
  const accessToken = authStorage.getItem('access_token');
  const csrfToken = authStorage.getItem('csrf_token');

  const isModifyingRequest =
    options?.method && ['POST', 'PUT', 'PATCH', 'DELETE'].includes(options.method.toUpperCase());

  if (requiresAuth && !accessToken) {
    throw new Error('Unauthorized');
  }

  // Check if the Authorization header is provided in options - useful for refreshing tokens to have the refresh token override the access token and get sent in the headers.
  const authHeader: HeadersInit = (options?.headers as Record<string, string>)?.Authorization
    ? {}
    : accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : {};

  const csrfHeader: HeadersInit =
    isModifyingRequest && csrfToken ? { 'X-CSRF-Token': csrfToken } : {};

  const isFormData = options?.body instanceof FormData;

  const headers: HeadersInit = {
    ...(isBlob || isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...authHeader,
    ...csrfHeader,
    ...options?.headers,
  };

  const mergedOptions: RequestInit = {
    ...options,
    headers,
    method: options?.method ?? 'GET',
  };

  const response = await api({ endpoint: url, options: mergedOptions });

  if (!response.ok) {
    // Try to extract error message from response body
    let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
    try {
      const errorData: ApiErrorResponse = await response.json();
      // Handle nested error structure: { error: { message: "..." } }
      const nestedErrorMessage =
        typeof errorData.error === 'object' ? errorData.error?.message : errorData.error;
      errorMessage = errorData.message ?? nestedErrorMessage ?? errorData.detail ?? errorMessage;
    } catch {
      // Response body is not JSON, use default message
    }
    throw new Error(errorMessage);
  }

  if (!response.ok) {
    if (response.status === 401 && requiresAuth) {
      // Clears tokens and notifies `logout` with `StorageEvent`
      AuthService.clearTokens();
      throw new Error('Tokens expired.');
    }

    // 400: Missing CSRF
    if (response.status === 400 && isModifyingRequest && !csrfToken) {
      await AuthService.CSRFToken();
      return fetcher<T>({ url, options, requiresAuth, isBlob });
    } else if (response.status === 403 && isModifyingRequest && csrfToken) {
      await AuthService.CSRFToken();
      return fetcher<T>({ url, options, requiresAuth, isBlob });
    }
    throw new FetchError(response);
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (isBlob ? await response.blob() : await response.json()) as T;
};
